/**
 * DepartmentsTab Component
 * Department management within Admin page
 */

import React, { useState } from 'react';
import { Plus, Edit, Trash2, Building2, Users } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Button, Input, Modal, LoadingSpinner, EmptyState } from '../../../components/common';
import apiClient from '../../../services/api';

interface Department {
  id: string;
  name: string;
  userCount: number;
}

export const DepartmentsTab: React.FC = () => {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState<string | null>(null);
  const [departmentName, setDepartmentName] = useState('');

  // Fetch departments (unique department values from users)
  const { data: departments, isLoading } = useQuery({
    queryKey: ['departments'],
    queryFn: async () => {
      const response = await apiClient.get<{ success: boolean; data: { departments: Department[] } }>('/users/departments');
      if (response.data.success) {
        return response.data.data.departments;
      }
      return [];
    },
  });

  // Create department mutation
  const createDepartment = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiClient.post('/users/departments', { name });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      toast.success('Department created successfully');
      setIsModalOpen(false);
      setDepartmentName('');
    },
    onError: () => {
      toast.error('Failed to create department');
    },
  });

  // Update department mutation
  const updateDepartment = useMutation({
    mutationFn: async ({ oldName, newName }: { oldName: string; newName: string }) => {
      const response = await apiClient.patch(
        `/users/departments/${encodeURIComponent(oldName)}`,
        { name: newName }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Department updated successfully');
      setIsModalOpen(false);
      setEditingDepartment(null);
      setDepartmentName('');
    },
    onError: () => {
      toast.error('Failed to update department');
    },
  });

  // Delete department mutation
  const deleteDepartment = useMutation({
    mutationFn: async (name: string) => {
      const response = await apiClient.delete(`/users/departments/${encodeURIComponent(name)}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['departments'] });
      queryClient.invalidateQueries({ queryKey: ['users'] });
      toast.success('Department deleted successfully');
    },
    onError: () => {
      toast.error('Failed to delete department');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!departmentName.trim()) return;

    if (editingDepartment) {
      updateDepartment.mutate({ oldName: editingDepartment, newName: departmentName.trim() });
    } else {
      createDepartment.mutate(departmentName.trim());
    }
  };

  const handleEdit = (name: string) => {
    setEditingDepartment(name);
    setDepartmentName(name);
    setIsModalOpen(true);
  };

  const handleDelete = (name: string, userCount: number) => {
    if (userCount > 0) {
      if (!window.confirm(`This department has ${userCount} users. Their department will be cleared. Continue?`)) {
        return;
      }
    } else if (!window.confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }
    deleteDepartment.mutate(name);
  };

  const openCreateModal = () => {
    setEditingDepartment(null);
    setDepartmentName('');
    setIsModalOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-stone-900" style={{ fontFamily: 'Fraunces, Georgia, serif' }}>Departments</h2>
          <p className="text-sm text-stone-500">
            Organize users into departments for better management
          </p>
        </div>
        <Button variant="primary" onClick={openCreateModal}>
          <Plus className="w-4 h-4 mr-2" />
          Add Department
        </Button>
      </div>

      {/* Departments List */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !departments?.length ? (
        <EmptyState
          icon={Building2}
          title="No departments yet"
          description="Create departments to organize your team members"
          action={{
            label: 'Add Department',
            onClick: openCreateModal,
          }}
        />
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {departments.map((dept) => (
            <Card key={dept.id} className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-stone-900">{dept.name}</h3>
                    <div className="flex items-center gap-1 text-sm text-stone-500">
                      <Users className="w-3 h-3" />
                      <span>{dept.userCount} {dept.userCount === 1 ? 'user' : 'users'}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => handleEdit(dept.name)}
                    className="p-2 text-stone-400 hover:text-primary-600 rounded-lg hover:bg-stone-100"
                    title="Edit department"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(dept.name, dept.userCount)}
                    className="p-2 text-stone-400 hover:text-danger-600 rounded-lg hover:bg-stone-100"
                    title="Delete department"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingDepartment(null);
          setDepartmentName('');
        }}
        title={editingDepartment ? 'Edit Department' : 'Add Department'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="department-name" className="block text-sm font-medium text-stone-700 mb-1">
              Department Name
            </label>
            <Input
              id="department-name"
              type="text"
              value={departmentName}
              onChange={(e) => setDepartmentName(e.target.value)}
              placeholder="e.g., Engineering, Marketing, Sales"
              autoFocus
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="secondary"
              onClick={() => {
                setIsModalOpen(false);
                setEditingDepartment(null);
                setDepartmentName('');
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={!departmentName.trim() || createDepartment.isPending || updateDepartment.isPending}
            >
              {createDepartment.isPending || updateDepartment.isPending ? (
                <LoadingSpinner size="sm" />
              ) : editingDepartment ? (
                'Update'
              ) : (
                'Create'
              )}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};
